import { useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/base/SEOHead';

export default function ContactPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL;
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    guests: '',
    vipTable: false,
    specialRequests: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": siteUrl
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Contact & Reservations",
            "item": `${siteUrl}/contact`
          }
        ]
      },
      {
        "@type": "LocalBusiness",
        "@id": `${siteUrl}/contact`,
        "name": "King Square Club",
        "description": "Reserve your VIP table at King Square Club, Benin City's premier luxury nightclub. Book exclusive experiences, premium bottle service, and unforgettable nights. Call +2349039844951.",
        "url": `${siteUrl}/contact`,
        "telephone": "+2349039844951",
        "priceRange": "₦₦₦",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "148 Airport Rd",
          "addressLocality": "Benin City",
          "postalCode": "300102",
          "addressRegion": "Edo",
          "addressCountry": "NG"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "6.3350",
          "longitude": "5.6037"
        },
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Friday", "Saturday", "Sunday"],
            "opens": "23:00",
            "closes": "05:00"
          }
        ],
        "potentialAction": {
          "@type": "ReserveAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": `${siteUrl}/contact`,
            "actionPlatform": [
              "http://schema.org/DesktopWebPlatform",
              "http://schema.org/MobileWebPlatform"
            ]
          },
          "result": {
            "@type": "Reservation",
            "name": "VIP Table Reservation"
          }
        }
      }
    ]
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('date', formData.date);
      formBody.append('time', formData.time);
      formBody.append('guests', formData.guests);
      formBody.append('vipTable', formData.vipTable ? 'Yes' : 'No');
      formBody.append('specialRequests', formData.specialRequests);

      const response = await fetch('https://readdy.ai/api/form/d6l8pt85dsf7sr1n5rd0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          date: '',
          time: '',
          guests: '',
          vipTable: false,
          specialRequests: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  return (
    <div className="min-h-screen bg-black">
      <SEOHead
        title="Contact & Reservations - King Square Club | Book Your VIP Table in Benin City"
        description="Reserve your VIP table at King Square Club, Benin City's premier luxury nightclub. Book exclusive experiences, premium bottle service, and unforgettable nights. Call +2349039844951."
        keywords="King Square Club contact, book VIP table Benin City, nightclub reservations Edo, luxury club booking Nigeria, VIP table Benin, contact King Square Club"
        canonical={`${siteUrl}/contact`}
        ogTitle="Contact & Reservations - King Square Club"
        ogDescription="Reserve your VIP table and experience the ultimate luxury nightlife. Book now for exclusive experiences."
        ogImage="https://public.readdy.ai/ai/img_res/f5524307fb8d179520e8cd6e5fa9e2fb.jpg"
        ogType="website"
        jsonLd={jsonLd}
      />
      <Navbar />
      
      <div className="pt-24 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-amber-400 mb-4">
              Reserve Your Table
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Experience the ultimate luxury nightlife at King Square Club. Book your VIP experience today.
            </p>
          </div>

          {/* Reservation Information Block */}
          <div className="mb-14 bg-gradient-to-br from-gray-900 to-black border border-amber-400/30 rounded-2xl p-10 shadow-2xl">
            <h2 className="text-3xl font-bold text-amber-400 mb-8 tracking-wide">Reservation Information</h2>

            <div className="grid lg:grid-cols-2 gap-10">
              {/* Left Column */}
              <div className="space-y-8">
                {/* Booking Time */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <span className="w-7 h-7 flex items-center justify-center bg-amber-400/10 rounded-full">
                      <i className="ri-time-line text-amber-400 text-base"></i>
                    </span>
                    Booking Time
                  </h3>
                  <ul className="space-y-2 pl-2">
                    <li className="flex items-start gap-2 text-gray-300 text-sm leading-relaxed">
                      <span className="text-amber-400 mt-1">–</span>
                      Please note after 12:30am we may have to give out your table.
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm leading-relaxed">
                      <span className="text-amber-400 mt-1">–</span>
                      Reservations can be made in advance, and we recommend booking at least 2 days before your desired date and time to ensure availability.
                    </li>
                  </ul>
                </div>

                {/* Reservation Policy */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <span className="w-7 h-7 flex items-center justify-center bg-amber-400/10 rounded-full">
                      <i className="ri-file-list-3-line text-amber-400 text-base"></i>
                    </span>
                    Reservation Policy
                  </h3>
                  <div className="space-y-5 pl-2">
                    <div>
                      <p className="text-amber-400 font-semibold text-sm mb-1">1. Cancellation / No Show Policy</p>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        – We understand that plans can change. Please notify us at least 2 hours in advance before your reservation time if you need to cancel or reschedule, and keep your deposit as credit for your next booking.
                      </p>
                    </div>
                    <div>
                      <p className="text-amber-400 font-semibold text-sm mb-1">2. Payment Policy</p>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        – Payment will be processed at the time of booking, or as specified in your reservation confirmation.
                      </p>
                    </div>
                    <div>
                      <p className="text-amber-400 font-semibold text-sm mb-1">3. Late Arrival</p>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        – Please arrive on time for your reservation. If you are running late, kindly inform us as soon as possible.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-8">
                {/* Dress Code */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <span className="w-7 h-7 flex items-center justify-center bg-amber-400/10 rounded-full">
                      <i className="ri-shirt-line text-amber-400 text-base"></i>
                    </span>
                    Dress Code
                  </h3>
                  <div className="pl-2 space-y-1">
                    <p className="text-amber-300 font-semibold text-sm">High Class and Dashing.</p>
                    <p className="text-gray-300 text-sm">– No slippers and shorts for men.</p>
                    <p className="text-gray-300 text-sm">– No slippers for ladies.</p>
                  </div>
                </div>

                {/* Table Pricing */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <span className="w-7 h-7 flex items-center justify-center bg-amber-400/10 rounded-full">
                      <i className="ri-price-tag-3-line text-amber-400 text-base"></i>
                    </span>
                    Table Pricing
                  </h3>
                  <div className="grid grid-cols-2 gap-4 pl-2">
                    <div className="bg-gradient-to-br from-amber-400/10 to-amber-600/5 border border-amber-400/40 rounded-xl p-3 sm:p-5 text-center">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center mx-auto mb-2 bg-amber-400/20 rounded-full">
                        <i className="ri-vip-crown-line text-amber-400 text-base sm:text-xl"></i>
                      </div>
                      <p className="text-amber-400 font-bold text-sm sm:text-base mb-1">Gold Table</p>
                      <p className="text-white font-extrabold text-sm sm:text-lg whitespace-nowrap">₦1,000,000</p>
                    </div>
                    <div className="bg-gradient-to-br from-gray-400/10 to-gray-500/5 border border-gray-400/40 rounded-xl p-3 sm:p-5 text-center">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center mx-auto mb-2 bg-gray-400/20 rounded-full">
                        <i className="ri-vip-crown-2-line text-gray-300 text-base sm:text-xl"></i>
                      </div>
                      <p className="text-gray-300 font-bold text-sm sm:text-base mb-1">Silver Table</p>
                      <p className="text-white font-extrabold text-sm sm:text-lg whitespace-nowrap">₦500,000</p>
                    </div>
                  </div>
                </div>

                {/* Thank You Note */}
                <div className="border-t border-amber-400/20 pt-6 pl-2">
                  <p className="text-gray-400 text-sm leading-relaxed italic">
                    Thank you for choosing <span className="text-amber-400 font-semibold not-italic">KING SQUARE CLUB</span>. We look forward to serving you and ensuring you have a memorable experience. If you have any questions or need further assistance, please don't hesitate to contact us.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Reservation Form */}
            <div className="bg-gradient-to-br from-gray-900 to-black border border-amber-400/20 rounded-2xl p-8 shadow-2xl">
              <h2 className="text-3xl font-bold text-amber-400 mb-6">Book Your Reservation</h2>
              
              <form id="reservation-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-gray-300 mb-2 font-medium">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-black/50 border border-amber-400/30 rounded-lg text-white text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-gray-300 mb-2 font-medium">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-black/50 border border-amber-400/30 rounded-lg text-white text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-gray-300 mb-2 font-medium">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-black/50 border border-amber-400/30 rounded-lg text-white text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    placeholder="08012345678"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="date" className="block text-gray-300 mb-2 font-medium">Date *</label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-black/50 border border-amber-400/30 rounded-lg text-white text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="time" className="block text-gray-300 mb-2 font-medium">Time *</label>
                    <input
                      type="time"
                      id="time"
                      name="time"
                      value={formData.time}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-black/50 border border-amber-400/30 rounded-lg text-white text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="guests" className="block text-gray-300 mb-2 font-medium">Number of Guests *</label>
                  <select
                    id="guests"
                    name="guests"
                    value={formData.guests}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-black/50 border border-amber-400/30 rounded-lg text-white text-sm focus:outline-none focus:border-amber-400 transition-colors"
                  >
                    <option value="">Select number of guests</option>
                    <option value="1-2">1-2 Guests</option>
                    <option value="3-5">3-5 Guests</option>
                    <option value="6-10">6-10 Guests</option>
                    <option value="10+">10+ Guests</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="vipTable"
                    name="vipTable"
                    checked={formData.vipTable}
                    onChange={handleChange}
                    className="w-5 h-5 bg-black/50 border border-amber-400/30 rounded text-amber-400 focus:ring-amber-400 focus:ring-2 cursor-pointer"
                  />
                  <label htmlFor="vipTable" className="ml-3 text-gray-300 font-medium cursor-pointer">
                    Request VIP Table
                  </label>
                </div>

                <div>
                  <label htmlFor="specialRequests" className="block text-gray-300 mb-2 font-medium">Special Requests</label>
                  <textarea
                    id="specialRequests"
                    name="specialRequests"
                    value={formData.specialRequests}
                    onChange={handleChange}
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 bg-black/50 border border-amber-400/30 rounded-lg text-white text-sm focus:outline-none focus:border-amber-400 transition-colors resize-none"
                    placeholder="Any special requests or occasions? (Max 500 characters)"
                  ></textarea>
                  <p className="text-gray-500 text-xs mt-1">{formData.specialRequests.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-amber-400 to-amber-600 text-black font-bold py-4 rounded-lg hover:from-amber-500 hover:to-amber-700 transition-all duration-300 shadow-lg hover:shadow-amber-400/50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Submitting...' : 'Confirm Reservation'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded-lg text-center">
                    Reservation submitted successfully! We'll contact you shortly.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg text-center">
                    Something went wrong. Please try again or contact us directly.
                  </div>
                )}
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              {/* Contact Details */}
              <div className="bg-gradient-to-br from-gray-900 to-black border border-amber-400/20 rounded-2xl p-8 shadow-2xl">
                <h2 className="text-3xl font-bold text-amber-400 mb-6">Contact Information</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-amber-400/10 rounded-lg mr-4 flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl text-amber-400"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">Location</h3>
                      <p className="text-gray-400">148 Airport Rd, Benin City 300102, Edo</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-amber-400/10 rounded-lg mr-4 flex-shrink-0">
                      <i className="ri-phone-line text-2xl text-amber-400"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">Phone</h3>
                      <a href="tel:09039844951" className="text-gray-400 hover:text-amber-400 transition-colors">
                        09039844951
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-amber-400/10 rounded-lg mr-4 flex-shrink-0">
                      <i className="ri-whatsapp-line text-2xl text-amber-400"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">WhatsApp</h3>
                      <a href="https://wa.me/2349039844951" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-amber-400 transition-colors">
                        09039844951
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-amber-400/10 rounded-lg mr-4 flex-shrink-0">
                      <i className="ri-time-line text-2xl text-amber-400"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">Opening Hours</h3>
                      <p className="text-gray-400">Friday - Sunday</p>
                      <p className="text-amber-400 font-semibold">11:00 PM - 5:00 AM</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="bg-gradient-to-br from-gray-900 to-black border border-amber-400/20 rounded-2xl p-8 shadow-2xl">
                <h2 className="text-3xl font-bold text-amber-400 mb-6">Follow Us</h2>
                
                <div className="grid grid-cols-2 gap-4">
                  <a
                    href="https://instagram.com/kingsquareclub"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-purple-500/50 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-instagram-line text-2xl mr-2"></i>
                    <span className="font-semibold">Instagram</span>
                  </a>

                  <a
                    href="https://snapchat.com/add/kingsquareclub"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center bg-gradient-to-r from-yellow-400 to-yellow-500 text-black py-4 rounded-lg hover:from-yellow-500 hover:to-yellow-600 transition-all duration-300 shadow-lg hover:shadow-yellow-500/50 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-snapchat-line text-2xl mr-2"></i>
                    <span className="font-semibold">Snapchat</span>
                  </a>

                  <a
                    href="https://tiktok.com/@kingsquareclub"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center bg-gradient-to-r from-gray-900 to-black border-2 border-white text-white py-4 rounded-lg hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-white/30 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-tiktok-line text-2xl mr-2"></i>
                    <span className="font-semibold">TikTok</span>
                  </a>

                  <a
                    href="https://wa.me/2349039844951"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center bg-gradient-to-r from-green-500 to-green-600 text-white py-4 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg hover:shadow-green-500/50 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line text-2xl mr-2"></i>
                    <span className="font-semibold">WhatsApp</span>
                  </a>
                </div>
              </div>

              {/* Map */}
              <div className="bg-gradient-to-br from-gray-900 to-black border border-amber-400/20 rounded-2xl p-8 shadow-2xl">
                <h2 className="text-3xl font-bold text-amber-400 mb-6">Find Us</h2>
                <div className="w-full h-80 rounded-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3964.0!2d5.6!3d6.3!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNsKwMTgnMDAuMCJOIDXCsDM2JzAwLjAiRQ!5e0!3m2!1sen!2sng!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="King Square Club Location"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
